import React, { useEffect, useState } from "react";
import { Empty, Tag, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, } from '@fortawesome/free-solid-svg-icons';
import { AppstoreOutlined, UnorderedListOutlined } from "@ant-design/icons";

import {
  setRefreshStatus,
  updateLeadPipelineStatus,
  setCreateSource,
  fetchLeads,
  updateLeadList,
} from "../../../Slices/salesSlice";

import SalesFilters from "../Sales1/SalesFilters";
import { useLeadListKey, useLeadListField } from "../Sales1/useLeadList";
import AddSales from "../Sales1/AddSales";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import PipelineKanban from "./PipelineKanban";

const Pipeline = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // 🔹 SALES STATE (same as Sales page)
  // This window's own pipeline list — independent from other Leads/Pipeline windows.
  const { listKey } = useLeadListKey();
  const salesLeads = useLeadListField(listKey, "rows");
  const page = useLeadListField(listKey, "page");
  const pageCount = useLeadListField(listKey, "pageCount");
  const prevVisibility = useLeadListField(listKey, "filterPanelOpen");
  const open = useLeadListField(listKey, "createOpen");
  const updateList = (changes) => dispatch(updateLeadList({ key: listKey, changes }));

    useEffect(() => {
      dispatch(setRefreshStatus(null));
      updateList({ createOpen: false, filterPanelOpen: false });
      // Always refetch pipeline data on mount/navigation to ensure fresh data.
      dispatch(fetchLeads({ isPipeline: true, listKey }));
    }, [location.pathname]);


  const createSource = useSelector((state) => state.sales.createSource);
  const [viewMode, setViewMode] = useState("kanban"); // "kanban" or "table"


  // ✅ PIPELINE DATA
  const pipelineLeads = salesLeads?.filter(
    (lead) => lead.isInPipeline === true
  );

  // 🔹 CREATE SALE (WORKING)
  const handleCreateSale = () => {
    updateList({ filterPanelOpen: false, createOpen: true });
    dispatch(setCreateSource("pipeline")); // track origin
  };


  // 🔹 Remove lead from pipeline
  const handleRemove = async (id) => {
    try {
      await dispatch(
        updateLeadPipelineStatus({
          leadId: id,
          isInPipeline: false,
        })
      ).unwrap();

      message.success("Lead removed from pipeline");
    } catch {
      message.error("Failed to remove lead from pipeline");
    }
  };

  // 🔹 Date formatter
  const formatDateTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp));
    return date.toLocaleString("en-IN", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // 🔹 Sorting + pagination
  const handleTableChange = (pagination, filters, sorter) => {
    dispatch(setRefreshStatus(null));

    const newSortObject = sorter?.order
      ? { [sorter.field]: sorter.order === "ascend" ? "asc" : "desc" }
      : {};

    updateList({ sort: newSortObject, page: pagination.current });
  };

  // 🔹 TABLE COLUMNS
  const columns = [
    { title: "S.No", dataIndex: "leadID", key: "leadID", width: 70, sorter: true },
    { title: "Agency Name", dataIndex: "agencyName", key: "agencyName", width: 140, sorter: true },
    { title: "State", dataIndex: "state", key: "state", width: 90, sorter: true },
    { title: "City", dataIndex: "city", key: "city", width: 90, sorter: true },
    { title: "Stage", dataIndex: "stage", key: "stage", width: 100, sorter: true },
    {
      title: "Follow-Up",
      dataIndex: "followupDate",
      key: "followupDate",
      width: 130,
      render: formatDateTime,
      sorter: true,
    },
    { title: "Time Zone", dataIndex: "timeZone", key: "timeZone", width: 90 },
    { title: "Status", dataIndex: "status", key: "status", width: 100 },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (p) => <Tag color={p === "High" ? "red" : "green"}>{p}</Tag>,
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdatedDate",
      key: "lastUpdatedDate",
      width: 150,
      render: formatDateTime,
      sorter: true,
    },
    {
      title: "Last Contacted",
      dataIndex: "lastContactedDate",
      key: "lastContactedDate",
      width: 150,
      render: formatDateTime,
      sorter: true,
    },
    { title: "Channel", dataIndex: "channel", key: "channel", width: 120 },
    { title: "Assigned To", dataIndex: "assignedTo", key: "assignedTo", width: 140 },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 12 }}>
          <a
            onClick={() =>
              navigate(`/sales/${record.id}`, { state: { from: "pipeline" } })
            }
          >
            <FontAwesomeIcon icon={faPen} />
          </a>

          <a style={{ color: "red" }} onClick={() => handleRemove(record.id)}>
            <FontAwesomeIcon icon={faTrash} />
          </a>
        </div >
      ),
    },
  ];

  return (
    <>
      {/* ✅ CREATE SALE PANEL */}
      {open && (
        <div className="page-wrapper">
          <div className="content container-fluid">
            <AddSales
              isCreate={true}
              handleClose={() => {
                updateList({ createOpen: false });


                navigate("/Pipeline");
              }}
            />
          </div>
        </div>
      )}



      {!open && (
        <div className="page-wrapper">
          <div className="content container-fluid">
            {/* { ✅ FILTER BAR WITH WORKING CREATE */}
            {<SalesFilters
              listRoute="/Pipeline"
            />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Sales Pipeline</h3>
              <div style={{ display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 8, padding: 2 }}>
                <button
                  onClick={() => setViewMode("kanban")}
                  style={{
                    padding: "4px 12px", fontSize: 13, border: "none", borderRadius: 6,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                    background: viewMode === "kanban" ? "#fff" : "transparent",
                    color: viewMode === "kanban" ? "#1677ff" : "#6b7280",
                    boxShadow: viewMode === "kanban" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    fontWeight: viewMode === "kanban" ? 600 : 400,
                  }}
                >
                  <AppstoreOutlined /> Board
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  style={{
                    padding: "4px 12px", fontSize: 13, border: "none", borderRadius: 6,
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
                    background: viewMode === "table" ? "#fff" : "transparent",
                    color: viewMode === "table" ? "#1677ff" : "#6b7280",
                    boxShadow: viewMode === "table" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                    fontWeight: viewMode === "table" ? 600 : 400,
                  }}
                >
                  <UnorderedListOutlined /> Table
                </button>
              </div>
            </div>

            {viewMode === "kanban" ? (
              <PipelineKanban leads={pipelineLeads} />
            ) : (
              <div className={`row ${prevVisibility ? "partial-width" : "full-width"}`}>
                <div className="table-responsive-wrapper">
                  <CustomGridTable
                    Id="sales-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={pipelineLeads || []}
                    scroll={{ x: 1400 }}
                    pagination={{
                      current: page,
                      pageSize: 50,
                      total: pageCount || 0,
                      showQuickJumper: true,
                      showSizeChanger: false,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                    size="small"
                    locale={{
                      emptyText: <Empty description="No leads in pipeline" />,
                    }}
                  />
                </div>
              </div>
            )}

          </div>
        </div>

      )}
    </>
  );
};

export default Pipeline;
